import { Button, Dropdown, Label } from "@heroui/react";
import { Icon } from "@iconify/react";
import { type Key } from "react";

export interface RowMenuAction {
  id: string;
  label: string;
  icon: string;
  isDanger?: boolean;
  isDisabled?: boolean;
  onAction?: () => void;
}

interface RowMenuProps {
  /** Names the row, e.g. "User actions". */
  label: string;
  actions: RowMenuAction[];
}

/** A list row's ⋯ menu; nothing renders without an action. */
export const RowMenu = ({ label, actions }: RowMenuProps) => {
  if (actions.length === 0) return null;

  const disabledKeys = actions
    .filter((action) => action.isDisabled)
    .map((action) => action.id);

  const handleAction = (key: Key) => {
    actions.find((action) => action.id === key)?.onAction?.();
  };

  return (
    <div className="flex justify-end">
      <Dropdown>
        <Dropdown.Trigger>
          <Button isIconOnly aria-label={label} size="sm" variant="tertiary">
            <Icon icon="solar:menu-dots-bold" />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Popover className="min-w-40" placement="bottom end">
          <Dropdown.Menu
            aria-label={label}
            disabledKeys={disabledKeys}
            onAction={handleAction}
          >
            {actions.map((action) => (
              <Dropdown.Item
                key={action.id}
                id={action.id}
                textValue={action.label}
                variant={action.isDanger ? "danger" : undefined}
              >
                <Icon
                  className={action.isDanger ? "text-lg" : "text-lg text-muted"}
                  icon={action.icon}
                />
                <Label>{action.label}</Label>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
};

interface RowActionsMenuProps {
  /** Names the row, e.g. "Product actions". */
  label: string;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

/** The common View / Edit / Delete menu; an action left out is not offered. */
export const RowActionsMenu = ({
  label,
  onView,
  onEdit,
  onDelete,
}: RowActionsMenuProps) => {
  const actions: RowMenuAction[] = [];

  if (onView)
    actions.push({
      id: "view",
      label: "View",
      icon: "solar:eye-linear",
      onAction: onView,
    });
  if (onEdit)
    actions.push({
      id: "edit",
      label: "Edit",
      icon: "solar:pen-linear",
      onAction: onEdit,
    });
  if (onDelete)
    actions.push({
      id: "delete",
      label: "Delete",
      icon: "solar:trash-bin-trash-linear",
      isDanger: true,
      onAction: onDelete,
    });

  return <RowMenu actions={actions} label={label} />;
};
